import { AlertTriangle, Check, Download, Eye, EyeOff, Loader2, Server, Share2, ShieldCheck, X } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { usePwaInstall, type PwaInstallPlatform } from "../../lib/pwaInstall";
import { PASSWORD_RULES, evaluatePassword } from "../../lib/passwordPolicy";
import { useOSTheme } from "../../lib/theme";
import { OkamiMark } from "../common/brand/OkamiMark";
import "./login.css";

type InstallPhase = "idle" | "checking" | "prompting";

/**
 * WHICH DOORS THIS DEPLOYMENT HAS (§8.2).
 *
 * The engine console accepted one identity — its local account — until
 * federated sign-in landed, and most deployments still do. So the page asks
 * rather than assuming: /api/auth/methods is public (auth.go's isPublicPath)
 * and says only which doors exist, never who may come through one.
 *
 * Three states, and the middle one matters. While the read is in flight the
 * provider button is NOT drawn — a button that appears a moment late is worse
 * than one that was never there, because an operator has already started
 * typing. A read that fails leaves the local form, which always works.
 */
type AuthMethods = { oidc: { enabled: boolean; name?: string; login_url?: string } };

function useAuthMethods(): AuthMethods | null {
  const [methods, setMethods] = useState<AuthMethods | null>(null);
  useEffect(() => {
    let live = true;
    if (typeof fetch !== "function") return;
    void (async () => {
      try {
        const r = await fetch("/api/auth/methods", { credentials: "same-origin" });
        if (!r.ok) return;
        const body = (await r.json()) as AuthMethods;
        if (live && body && typeof body === "object" && body.oidc) setMethods(body);
      } catch {
        // The local form is the fallback and it needs no announcement.
      }
    })();
    return () => {
      live = false;
    };
  }, []);
  return methods;
}

/**
 * WHAT THIS PAGE MAY HONESTLY SAY ABOUT THE DEPLOYMENT.
 *
 * The board names the estate, its host count and its region. Nobody is signed
 * in here, so none of those three is available: entries/login.tsx hydrates no
 * tenant scope on purpose, and engine/internal/api/auth.go's isPublicPath
 * admits only /login, /api/login, /api/logout, the favicons and the two health
 * probes to an unauthenticated caller — no roster, no inventory.
 *
 * The origin, on the other hand, is a fact the browser holds without asking
 * anyone. It is the half of the board's intent that survives: an operator with
 * three estates open in three tabs needs to know which one this tab signs into.
 */
function deploymentOrigin(): string | null {
  const host = typeof window === "undefined" ? "" : window.location.host;
  return host.trim() === "" ? null : host;
}

/**
 * THE BUILD, WHICH IS THE VERSION QUESTION ASKED ON EVERY INCIDENT CALL.
 *
 * Three states, and the third is the one that matters. /api/version is NOT a
 * public path: auth.go:296 lets it through unauthenticated only when
 * edge.LocalUnproxied(r), so a console served through nginx answers 401 to this
 * read and the honest footer says the build was NOT REPORTED. It never says a
 * version it did not receive, and it never renders the absence as a blank.
 */
type BuildState = { status: "reading" } | { status: "unreported" } | { status: "reported"; build: string };

function useEngineBuild(): BuildState {
  const [state, setState] = useState<BuildState>({ status: "reading" });

  useEffect(() => {
    let live = true;
    const settle = (next: BuildState) => {
      if (live) setState(next);
    };

    if (typeof fetch !== "function") {
      settle({ status: "unreported" });
      return () => {
        live = false;
      };
    }

    // No shared api client: lib/api.ts funnels reads through the tenant-scope
    // barrier and treats a 401 as "your session went away", neither of which
    // applies to a page whose whole premise is that there is no session.
    void (async () => {
      try {
        const response = await fetch("/api/version", {
          credentials: "same-origin",
          headers: { Accept: "application/json" }
        });
        if (!response.ok) {
          settle({ status: "unreported" });
          return;
        }
        const body: unknown = await response.json();
        const build = readBuild(body);
        settle(build === null ? { status: "unreported" } : { status: "reported", build });
      } catch {
        settle({ status: "unreported" });
      }
    })();

    return () => {
      live = false;
    };
  }, []);

  return state;
}

/**
 * The engine publishes `build` — the release tag when it has one, the short
 * revision otherwise, `-dirty` appended when the tree was not clean
 * (engine/internal/buildinfo). A missing or blank field is NOT reported; it is
 * never filled in from `sha`, which is a hash of the embedded frontend assets
 * and answers a different question.
 */
function readBuild(body: unknown): string | null {
  if (typeof body !== "object" || body === null) return null;
  const build = (body as { build?: unknown }).build;
  if (typeof build !== "string") return null;
  const trimmed = build.trim();
  return trimmed === "" || trimmed === "unknown" ? null : trimmed;
}

export function LoginPage() {
  /**
   * The destination the console asked us to return to, read from our own URL.
   *
   * NOT validated here beyond being a same-origin path — the server does that
   * properly (internal/safeurl.Next), and a client-side check on an open
   * redirect is a comment rather than a control. This only decides whether to
   * carry the value at all.
   */
  const nextDestination = (() => {
    const raw = new URLSearchParams(window.location.search).get("next") ?? "";
    if (!raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return "";
    return raw;
  })();

  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  // Theme follows the OS and nothing else — see src/lib/theme.ts. The hook keeps
  // the document classes + favicon in sync and re-renders on an OS change; the
  // login page has no other use for the value.
  useOSTheme();
  const ruleState = useMemo(() => evaluatePassword(password), [password]);
  const [installOpen, setInstallOpen] = useState(false);
  const [installPhase, setInstallPhase] = useState<InstallPhase>("idle");
  const install = usePwaInstall();
  const hasError = useMemo(() => new URLSearchParams(window.location.search).get("err") === "1", []);
  const methods = useAuthMethods();
  const oidc = methods?.oidc.enabled ? methods.oidc : null;
  /**
   * WHY THE PROVIDER REFUSED, in the provider's own word.
   *
   * The callback sends the browser back here with ?err=oidc&reason=… rather
   * than a bare 4xx, because "not_authorized" and "access_denied" are
   * different things to do next: the first needs an administrator, the second
   * needs the operator to try again. Saying "sign-in failed" for both is the
   * unhelpful average.
   */
  const oidcError = useMemo(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("err") !== "oidc") return null;
    const reason = q.get("reason") ?? "";
    if (reason === "not_authorized") {
      return "That account signed in, but it does not hold the role this engine requires. Ask an administrator to grant it.";
    }
    if (reason === "access_denied") return "The sign-in was cancelled at the identity provider.";
    return reason
      ? `The identity provider refused the sign-in (${reason}).`
      : "The identity provider did not complete the sign-in.";
  }, []);
  const origin = useMemo(() => deploymentOrigin(), []);
  const build = useEngineBuild();
  const installBusy = install.preparing || installPhase !== "idle";
  const installLabel =
    install.preparing
      ? "Preparing install"
      : installPhase === "checking"
        ? "Checking install"
        : installPhase === "prompting"
          ? "Opening install"
          : "Install app";

  // DERIVED, never a literal: the checklist header states how many of the
  // engine's rules are satisfied, and both numbers come from PASSWORD_RULES.
  const metCount = PASSWORD_RULES.filter((rule) => ruleState[rule.id]).length;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    const form = event.currentTarget;
    // THE SERVER DECIDES (forms plan §3.18, F34). Only the browser's own
    // required check stands in the way here. Sign in used to be dead until
    // this page's copy of the password policy was met, so a stored credential
    // that predates a rule, or a rule read wrong here, locked the operator out
    // with no word from the server. The engine enforces the policy on the
    // stored credential (engine/internal/api/passwordpolicy.go) and answers a
    // wrong password itself; the checklist below informs, it does not gate.
    if (!form.checkValidity()) {
      event.preventDefault();
    }
  }

  async function handleInstallClick(): Promise<void> {
    if (installBusy) return;
    setInstallOpen(false);
    try {
      if (install.available) {
        setInstallPhase("prompting");
        const prompted = await install.promptInstall();
        if (!prompted) setInstallOpen(true);
        return;
      }

      setInstallPhase("checking");
      const promptReady =
        install.platform === "desktop-chromium" || install.platform === "android-browser"
          ? await install.waitForPrompt(2200)
          : await delay(650).then(() => false);
      if (promptReady) {
        setInstallPhase("prompting");
        const prompted = await install.promptInstall();
        if (!prompted) setInstallOpen(true);
        return;
      }
      setInstallOpen(true);
    } finally {
      setInstallPhase("idle");
    }
  }

  return (
    <main className="login-page">
      {/* The board budgets no floating region and this one stays: it is a real
          control with browser-level coverage behind it, and a board is a skin.
          It keeps the corner rather than taking a slot in the 396px column. */}
      <div className="login-utility">
        {!install.installed ? (
          <button
            type="button"
            title={installLabel}
            aria-label={installLabel}
            aria-busy={installBusy}
            disabled={installBusy}
            className="login-button"
            onClick={() => void handleInstallClick()}
          >
            {installBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className={installBusy ? "inline" : "hidden sm:inline"}>
              {install.preparing
                ? "Preparing..."
                : installPhase === "checking"
                  ? "Checking..."
                  : installPhase === "prompting"
                    ? "Opening..."
                    : "Install"}
            </span>
          </button>
        ) : null}
      </div>

      <div className="login-column">
        {/* The product lockup. It lives in the rail on every other page
            (features/soc/Sidebar.tsx) and the rail is the one thing this page
            does not have, so without it the sign-in page never says what
            product it is. */}
        <div className="login-brand">
          {/* The rail's mark, the Okami wolf, at the lockup's larger size (login.css). */}
          <OkamiMark className="login-brand-mark" />
          <div className="login-brand-name">
            <strong>Okami</strong>
            <span>Runtime security</span>
          </div>
        </div>

        <section className="login-card">
          <h1>Sign in</h1>
          {/* Not "the Go-managed session cookie and CSRF model", which described
              the implementation to the one audience that cannot act on it. What
              an operator needs to know is what this console commands. No
              session lifetime is quoted: the engine's is 24h absolute
              (auth.go defaultSessionTTL) and the control plane's BFF is 8h
              (bff.go sessionTTL), so a single number printed here would be
              wrong on one of the two deployments. */}
          <p className="login-lede">
            Runtime detection and containment for the hosts on this deployment. Operator credentials only.
          </p>

          {hasError ? (
            <div className="login-alert" role="alert">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              <span>Invalid credentials.</span>
            </div>
          ) : null}
          {oidcError ? (
            <div className="login-alert" role="alert">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              <span>{oidcError}</span>
            </div>
          ) : null}

          {/* THE PROVIDER FIRST, because on a federated deployment it is the
              way in for everyone but whoever holds the local credential —
              which is the console's break-glass, not its front door. It is a
              LINK, not a button in the form: the flow is a redirect the
              server starts, and the destination rides along so a deep link
              survives the round trip. */}
          {oidc ? (
            <>
              <a
                className="login-button login-button--primary login-oidc"
                href={
                  oidc.login_url +
                  (nextDestination ? `?next=${encodeURIComponent(nextDestination)}` : "")
                }
              >
                <ShieldCheck size={16} aria-hidden="true" />
                Sign in with {oidc.name ?? "your identity provider"}
              </a>
              <div className="login-or">
                <span>or sign in with the local account</span>
              </div>
            </>
          ) : null}

          {/* No form-level autocomplete="off" (F34): each field carries its own
              token (username, current-password), so a password manager fills
              the sign-in it saved. */}
          <form method="POST" action="/api/login" onSubmit={handleSubmit} className="login-form">
            {/* WHERE THEY WERE GOING, carried across the sign-in.
                The console appends ?next=<location> when a 401 sends someone
                here, and without this hidden field the form post drops it and
                the engine lands everyone on "/" — so a bookmarked surface signs
                you in and shows you the dashboard. The server validates it
                (internal/safeurl); this only has to not lose it. */}
            {nextDestination ? <input type="hidden" name="next" value={nextDestination} /> : null}
            {/* USERNAME, not Email. The board labels this field Email; the
                server authenticates a username (engine/internal/api/auth.go
                HandleLogin reads the `user` form field against the stored
                operator credential), and relabelling it would tell every
                operator to type something that cannot sign them in. */}
            <label className="login-field">
              <span>Username</span>
              <div className="login-input">
                <input name="user" autoComplete="username" required autoFocus />
              </div>
            </label>

            <label className="login-field">
              <span>Password</span>
              <div className="login-input">
                <input
                  name="pass"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  aria-describedby="password-policy"
                />
                <button
                  type="button"
                  className="login-reveal"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((value) => !value)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>

            {/* THE POLICY CHECKLIST STAYS, as information. The board omits it;
                the engine enforces the same rules on the stored credential at
                startup, and the tests and the live sign-in flow read it. Since
                F34 it gates nothing: the server decides. */}
            <div id="password-policy" aria-live="polite" className="login-policy">
              <div className="login-policy-head">
                <span>Password requirements</span>
                <span
                  className="login-policy-count"
                  data-complete={metCount === PASSWORD_RULES.length ? "true" : "false"}
                >
                  {metCount}/{PASSWORD_RULES.length} met
                </span>
              </div>
              <ul className="login-policy-list">
                {PASSWORD_RULES.map((rule) => {
                  const met = ruleState[rule.id];
                  return (
                    <li key={rule.id} data-met={met ? "true" : "false"}>
                      <span aria-hidden="true" className="login-tick">
                        {met ? <Check className="h-3 w-3" /> : null}
                      </span>
                      <span>{rule.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <button type="submit" className="login-button login-button--primary">
              Sign in
            </button>
          </form>

          {/* The board's estate banner, carrying the fact this page can stand
              behind. See deploymentOrigin() for the three it cannot. */}
          <div className="login-deployment">
            <Server size={15} aria-hidden="true" />
            {origin ? (
              <span>
                You are signing in to <b>{origin}</b>
              </span>
            ) : (
              <span>This page cannot name the deployment it is served from.</span>
            )}
          </div>
        </section>

        <footer className="login-foot">
          <span>Trouble signing in? Contact your SOC administrator.</span>
          <BuildStamp state={build} />
        </footer>
      </div>

      {installOpen ? <InstallHelp platform={install.platform} onClose={() => setInstallOpen(false)} /> : null}
    </main>
  );
}

/** The build line. Absent is stated, never blanked and never invented. */
function BuildStamp({ state }: { state: BuildState }) {
  if (state.status === "reported") {
    return (
      <b className="login-build" data-build="reported">
        {state.build}
      </b>
    );
  }
  const reading = state.status === "reading";
  return (
    <b
      className="login-build"
      data-build={reading ? "reading" : "unreported"}
      title={
        reading
          ? "Asking the engine which build it is running"
          : "This deployment does not publish /api/version to an unauthenticated caller"
      }
    >
      {reading ? "Reading build" : "Build not reported"}
    </b>
  );
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function InstallHelp({ platform, onClose }: { platform: PwaInstallPlatform; onClose: () => void }) {
  const copy =
    platform === "ios-browser"
      ? {
          title: "Install on iPhone or iPad",
          body: "Tap Share, then Add to Home Screen. If iOS shows Open as Web App, leave it enabled before tapping Add.",
          note: "The installed app opens as its own web app from the Home Screen and device search.",
        }
      : platform === "android-browser"
        ? {
            title: "Install on Android",
            body: "Use a normal Chrome or Edge tab, open the browser menu, then choose Install app. If prompted, keep the app install option selected instead of a plain shortcut.",
            note: "Incognito and temporary profiles cannot create the persistent app. A real installed PWA remains available from the launcher and search.",
          }
        : platform === "desktop-chromium"
          ? {
              title: "Use a regular Edge or Chrome profile",
              body: "Close InPrivate, Incognito, and Guest windows. Open this URL in a normal Edge or Chrome profile, then use the address-bar app icon or choose Apps, then Install this site as an app.",
              note: "Private and Guest profiles cannot create the persistent macOS app, even when the PWA itself is valid.",
            }
          : platform === "desktop-safari"
            ? {
                title: "Install on Safari",
                body: "Use File, then Add to Dock when Safari offers it.",
                note: "The app manifest, icons, and offline worker are already published.",
              }
            : {
                title: "Install app",
                body: "Open the browser menu and choose Install app or Add to Home Screen.",
                note: "If the browser only creates a bookmark or shortcut, removing that icon also removes the launcher entry.",
              };

  return (
    <div
      className="login-scrim"
      role="dialog"
      aria-modal="true"
      aria-labelledby="install-help-title"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <section className="login-dialog">
        <div className="login-dialog-head">
          <h2 id="install-help-title">{copy.title}</h2>
          <button type="button" aria-label="Close install help" className="login-dialog-close" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="login-dialog-body">
          <Share2 className="h-4 w-4" aria-hidden="true" />
          <div>
            <p>{copy.body}</p>
            <p>{copy.note}</p>
          </div>
        </div>
        <button type="button" className="login-button login-button--primary" onClick={onClose}>
          Done
        </button>
      </section>
    </div>
  );
}
